import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  CSSProperties,
  ReactNode,
} from "react";
import { BrandWatermark, type BrandWatermarkProps } from "../shared/BrandWatermark";
import styles from "./primitives.module.css";

/*
 * Primitives YELLOW+ : les briques avec lesquelles on écrit tout nouveau
 * bloc. Elles ne consomment que les tokens --paf-* (src/styles/paf-tokens.css).
 * Une Section pose le ton (fond + couleurs de texte) ; tout ce qu'elle contient
 * hérite via --paf-fg / --paf-heading / --paf-accent.
 */

/** Trois fonds de section, et rien d'autre (charte du 14/10/2026 : le crème est supprimé). */
export type SectionTone = "white" | "grey" | "dark";

/** Respiration verticale d'un bord de section : 0, la moitié, la valeur de charte, ou la version longue. */
export type SectionPad = "none" | "compact" | "normal" | "airy";

export type SectionProps = {
  tone?: SectionTone;
  /**
   * Le filigrane de marque, le symbole `[Y]` débordant d'un coin à très faible
   * opacité. Il est posé AUTOMATIQUEMENT sur `tone="dark"` depuis le 24/09/2026,
   * parce que c'est là que l'aplat a besoin d'être cassé : Pierre n'a pas à le
   * demander section par section. `false` le retire, un objet le règle.
   * Sur les fonds clairs il n'apparaît jamais tout seul : il faut le passer.
   */
  watermark?: boolean | BrandWatermarkProps;
  /** Demi-padding vertical, en haut comme en bas. Raccourci de `padTop`/`padBottom` à "compact". */
  compact?: boolean;
  /** Respiration du haut, prioritaire sur `compact`. */
  padTop?: SectionPad;
  /** Respiration du bas, prioritaire sur `compact`. */
  padBottom?: SectionPad;
  /** Sans Container : le contenu occupe toute la largeur (marquee, bandeau…). */
  bleed?: boolean;
  narrow?: boolean;
  id?: string;
  as?: "section" | "div" | "header" | "footer";
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
};

export function Section({
  tone = "white",
  watermark,
  compact = false,
  padTop,
  padBottom,
  bleed = false,
  narrow = false,
  id,
  as: Tag = "section",
  className,
  style,
  children,
}: SectionProps) {
  /* Automatique sur l'encre, jamais ailleurs sans demande explicite.

     La taille est calée en POURCENTAGE de la section, pas en valeur fixe : un
     symbole de 460 px sur une section de 1440 se lit comme un accident, pas
     comme un geste. Akiro pousse le sien au-delà de 1800 px de large. Le
     plafond en px empêche seulement qu'il devienne absurde sur un très grand
     écran. */
  const showWatermark = watermark === undefined ? tone === "dark" : watermark !== false;
  const watermarkProps: BrandWatermarkProps = {
    width: "min(48%, 820px)",
    ...(typeof watermark === "object" ? watermark : null),
  };
  return (
    <Tag
      id={id}
      className={[styles.section, className].filter(Boolean).join(" ")}
      data-tone={tone}
      data-compact={compact || undefined}
      data-pad-top={padTop}
      data-pad-bottom={padBottom}
      data-watermark={showWatermark || undefined}
      style={style}
    >
      {showWatermark ? <BrandWatermark {...watermarkProps} /> : null}
      {bleed ? children : <Container narrow={narrow}>{children}</Container>}
    </Tag>
  );
}

export type ContainerProps = {
  narrow?: boolean;
  className?: string;
  children: ReactNode;
};

export function Container({ narrow = false, className, children }: ContainerProps) {
  return (
    <div
      className={[styles.container, className].filter(Boolean).join(" ")}
      data-narrow={narrow || undefined}
    >
      {children}
    </div>
  );
}

export type HeadingSize = "display" | "h1" | "h2" | "h3" | "h4";

export type HeadingProps = {
  /** Balise sémantique (SEO), indépendante de la taille visuelle. */
  as?: "h1" | "h2" | "h3" | "h4" | "p";
  size?: HeadingSize;
  align?: "left" | "center";
  id?: string;
  className?: string;
  /** UN mot à surligner (jamais une ligne entière) : <Heading>Plus de <mark>clients</mark></Heading> */
  children: ReactNode;
};

export function Heading({
  as: Tag = "h2",
  size = "h2",
  align = "left",
  id,
  className,
  children,
}: HeadingProps) {
  return (
    <Tag
      id={id}
      className={[styles.heading, className].filter(Boolean).join(" ")}
      data-size={size}
      data-align={align}
    >
      {children}
    </Tag>
  );
}

export type TextProps = {
  as?: "p" | "div" | "span";
  muted?: boolean;
  size?: "base" | "small";
  align?: "left" | "center";
  className?: string;
  children: ReactNode;
};

export function Text({
  as: Tag = "p",
  muted = false,
  size = "base",
  align = "left",
  className,
  children,
}: TextProps) {
  return (
    <Tag
      className={[styles.text, className].filter(Boolean).join(" ")}
      data-muted={muted || undefined}
      data-size={size}
      data-align={align}
    >
      {children}
    </Tag>
  );
}

export type ButtonVariant = "primary" | "secondary" | "dark";

type ButtonBaseProps = {
  variant?: ButtonVariant;
  /** Flèche → qui glisse au survol. */
  arrow?: boolean;
  /** Icône (SVG) placée avant le libellé, ex. un calendrier. */
  icon?: ReactNode;
  className?: string;
  children: ReactNode;
};

export type ButtonProps = ButtonBaseProps &
  (
    | ({ href: string } & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href" | "className" | "children">)
    | ({ href?: undefined } & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className" | "children">)
  );

/** Bouton de la charte : 18/500, tracking 1 px, padding 16/24, radius 6. */
export function Button(props: ButtonProps) {
  const { variant = "primary", arrow = false, icon, className, children, ...rest } = props;
  const classes = [styles.button, className].filter(Boolean).join(" ");
  const content = (
    <>
      {icon ? (
        <span className={styles.buttonIcon} aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <span>{children}</span>
      {arrow ? (
        <span className={styles.buttonArrow} aria-hidden="true">
          →
        </span>
      ) : null}
    </>
  );
  if ("href" in rest && typeof rest.href === "string") {
    const { href, ...anchor } = rest as { href: string } & AnchorHTMLAttributes<HTMLAnchorElement>;
    return (
      <a href={href} className={classes} data-variant={variant} {...anchor}>
        {content}
      </a>
    );
  }
  const { type = "button", ...button } = rest as ButtonHTMLAttributes<HTMLButtonElement>;
  return (
    <button type={type} className={classes} data-variant={variant} {...button}>
      {content}
    </button>
  );
}

export type CardProps = {
  /** Force le gris clair (texte encre) quel que soit le fond. La carte blanche à filet n'existe plus (14/10/2026). */
  tone?: "grey";
  /** Filigrane de marque, à demander : sur une petite surface il ne s'impose pas. */
  watermark?: boolean | BrandWatermarkProps;
  as?: "div" | "article" | "li";
  className?: string;
  children: ReactNode;
};

/**
 * Carte, sans aucune bordure (charte du 14/10/2026). SANS `tone`, elle suit le
 * fond de sa section : gris clair plein sur une section blanche, gris foncé
 * plein (--paf-card-dark) sur une section noire, blanc plein sur une section
 * grise (combinaison à éviter). Rayon unique : --paf-radius-card (12 px).
 */
export function Card({ tone, watermark, as: Tag = "div", className, children }: CardProps) {
  return (
    <Tag
      className={[styles.card, className].filter(Boolean).join(" ")}
      data-tone={tone}
      data-watermark={watermark ? "true" : undefined}
    >
      {watermark ? <BrandWatermark {...(typeof watermark === "object" ? watermark : null)} /> : null}
      {children}
    </Tag>
  );
}

export type StatProps = {
  value: ReactNode;
  label: ReactNode;
  /**
   * Sous-composant optionnel : le DOUBLE détouré, une copie du chiffre décalée
   * derrière lui, intérieur transparent et contour à faible opacité. C'est
   * l'effet du compteur de `ClientShowcase` (« 40+ entreprises locales »),
   * remonté ici le 21/09/2026 pour être disponible sur n'importe quel chiffre.
   * `value` doit alors être un texte, puisqu'il est dupliqué.
   */
  ghost?: boolean;
  className?: string;
};

/** Chiffre clé : Inter 64/800 (jaune sur fond sombre) + libellé. Jamais de Thunder (retirée de la charte le 14/10/2026). */
export function Stat({ value, label, ghost = false, className }: StatProps) {
  return (
    <div className={[styles.stat, className].filter(Boolean).join(" ")}>
      <span className={styles.statValue} data-ghost={ghost ? "true" : undefined}>
        {ghost ? (
          <span className={styles.statGhost} aria-hidden="true">
            {value}
          </span>
        ) : null}
        {value}
      </span>
      <span className={styles.statLabel}>{label}</span>
    </div>
  );
}

export type PillProps = {
  as?: "span" | "p";
  /** `outline` (défaut) : filet fin ; `solid` : fond jaune. */
  variant?: "outline" | "solid";
  className?: string;
  children: ReactNode;
};

/** Étiquette en capitales (eyebrow « SOLUTIONS MÉTIERS »), contour fin ou fond jaune. */
export function Pill({ as: Tag = "span", variant = "outline", className, children }: PillProps) {
  return (
    <Tag className={[styles.pill, className].filter(Boolean).join(" ")} data-variant={variant}>
      {children}
    </Tag>
  );
}
